import createClient from 'openapi-fetch';
import { SESSION_COOKIE_HEADER, tokens } from '../auth/tokens';
import { ApiError } from './errors';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const NO_REFRESH = [
  '/v1/auth/login',
  '/v1/auth/refresh',
  '/v1/auth/signup',
  '/v1/auth/accept-invitation',
];

/** Avisos de "tu empresa exige MFA y no la tienes": la app redirige a Mi seguridad. */
const MFA_REQUIRED = 'auth.mfa_enrollment_required';
const mfaListeners = new Set<() => void>();

export function onMfaEnrollmentRequired(listener: () => void): () => void {
  mfaListeners.add(listener);
  return () => mfaListeners.delete(listener);
}

async function detectMfaRequired(response: Response): Promise<Response> {
  if (response.status === 403) {
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => null);
    if (body && typeof body === 'object' && (body as { code?: unknown }).code === MFA_REQUIRED)
      mfaListeners.forEach((l) => l());
  }
  return response;
}

/**
 * fetch con credenciales: agrega el access token y, ante un 401, renueva la sesión una vez y
 * reintenta la petición original (clonada antes del primer intento porque el cuerpo se consume).
 * Todas las peticiones piden la sesión en cookie HttpOnly (login, registro, cambio de empresa,
 * aceptar invitación y cerrar sesión la ponen o la borran).
 */
export async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone();
  const withToken = (request: Request) => {
    const prepared = new Request(request, { credentials: 'include' });
    prepared.headers.set(SESSION_COOKIE_HEADER, '1');
    const token = tokens.access;
    if (token) prepared.headers.set('Authorization', `Bearer ${token}`);
    return prepared;
  };
  const response = await fetch(withToken(input));
  const path = new URL(input.url, window.location.origin).pathname;
  if (response.status !== 401 || NO_REFRESH.includes(path)) return detectMfaRequired(response);
  const session = await tokens.refresh();
  if (!session) return response;
  const retried = await fetch(withToken(retry));
  // Recién renovada y aun así rechazada: la sesión no sirve. Se cierra para que el panel muestre
  // el ingreso en vez de quedarse cargando y renovando sin fin.
  if (retried.status === 401) tokens.clear();
  return detectMfaRequired(retried);
}

export const client = createClient<paths>({ baseUrl: API_BASE, fetch: authFetch });

/** Convierte el resultado de openapi-fetch en datos o en ApiError (RFC 9457). */
export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (!response.ok) throw await ApiError.fromResponse(response, error);
  return data as T;
}

/** Clave para `Idempotency-Key`: reintentos seguros de operaciones que cambian estado. */
export function idempotencyKey(): string {
  return crypto.randomUUID();
}
